import { useContext, useEffect, useRef, useState } from 'react';
import { Tab, Tabs, useMediaQuery } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { CustomTabPanel } from '../../presentation-components/display-elements-components/custom-tab-panel';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { LoadingPage } from '../loading-page';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import gaiaTheme from '../../../root-components/theme';
import SuperAdminService, {
  IFriendlyCategory,
  IFriendlyCategoryGroup,
  IPostFriendlyCategory,
  IPostFriendlyCategoryGroup,
} from '../../../shared/services/super-admin/super-admin.service';
import { AddEditFriendlyCategoryGroupDialog } from '../../dialogs/super-admin/add-edit-friendly-category-group-dialog';
import { AddEditFriendlyCategoryDialog } from '../../dialogs/super-admin/add-edit-friendly-category-dialog';

export const SuperAdminEmissionCategoriesPage = () => {
  const superAdminService = new SuperAdminService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogSubmitting, setIsDialogSubmitting] = useState(false);

  // Groups state
  const [groups, setGroups] = useState<IFriendlyCategoryGroup[]>([]);
  const [allGroups, setAllGroups] = useState<IFriendlyCategoryGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IFriendlyCategoryGroup>();
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [totalGroups, setTotalGroups] = useState(0);
  const [pageGroups, setPageGroups] = useState(0);
  const [perPageGroups, setPerPageGroups] = useState(10);
  const [orderByGroups, setOrderByGroups] = useState<string>();
  const [orderGroups, setOrderGroups] = useState<'asc' | 'desc'>();
  const [changeGroups, setChangeGroups] = useState(0);
  const requestIdGroups = useRef(0);

  // Categories state
  const [categories, setCategories] = useState<IFriendlyCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<IFriendlyCategory>();
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [totalCategories, setTotalCategories] = useState(0);
  const [pageCategories, setPageCategories] = useState(0);
  const [perPageCategories, setPerPageCategories] = useState(10);
  const [orderByCategories, setOrderByCategories] = useState<string>();
  const [orderCategories, setOrderCategories] = useState<'asc' | 'desc'>();
  const [changeCategories, setChangeCategories] = useState(0);
  const requestIdCategories = useRef(0);

  useEffect(() => {
    if (tab === 0) {
      loadGroups();
    } else {
      loadCategories();
    }
  }, [tab, pageGroups, perPageGroups, changeGroups, orderByGroups, orderGroups, pageCategories, perPageCategories, changeCategories, orderByCategories, orderCategories]);

  useEffect(() => {
    superAdminService.getAllFriendlyCategoryGroupsWithPagination(0, 100)
      .then(r => setAllGroups(r.rows))
      .catch(err => console.log(err));
  }, []);

  const loadGroups = () => {
    requestIdGroups.current++;
    const id = requestIdGroups.current;
    setIsLoading(true);
    superAdminService.getAllFriendlyCategoryGroupsWithPagination(pageGroups, perPageGroups, orderByGroups, orderGroups)
      .then(r => {
        if (id === requestIdGroups.current) {
          setGroups(r.rows);
          setTotalGroups(r.count);
          setIsLoading(false);
        }
      })
      .catch(err => { console.log(err); setIsLoading(false); });
  };

  const loadCategories = () => {
    requestIdCategories.current++;
    const id = requestIdCategories.current;
    setIsLoading(true);
    superAdminService.getAllFriendlyCategoriesWithPagination(pageCategories, perPageCategories, orderByCategories, orderCategories)
      .then(r => {
        if (id === requestIdCategories.current) {
          setCategories(r.rows);
          setTotalCategories(r.count);
          setIsLoading(false);
        }
      })
      .catch(err => { console.log(err); setIsLoading(false); });
  };

  // Group table
  const groupHeadCells: IHeadCell[] = [
    { id: 'action', label: 'Action', numeric: false, sortable: false },
    { id: 'name', label: 'Name', numeric: false, sortable: true },
    { id: 'displayOrder', label: 'Order', numeric: true, sortable: true },
    { id: 'icon', label: 'Icon', numeric: false, sortable: false },
  ];

  const groupRows = groups.map(g => ({
    id: g.id,
    action: (
      <TableActionButtons
        id={g.id}
        includeEdit
        onEdit={() => { setSelectedGroup(g); setOpenGroupDialog(true); }}
      />
    ),
    name: g.name,
    displayOrder: g.displayOrder,
    icon: g.icon || '',
  }));

  const submitGroupHandler = (values: IPostFriendlyCategoryGroup, resetForm: () => void, isEdit: boolean) => {
    if (isDialogSubmitting) return;
    setIsDialogSubmitting(true);
    const action = isEdit && selectedGroup
      ? superAdminService.updateFriendlyCategoryGroup(selectedGroup.id, values)
      : superAdminService.createFriendlyCategoryGroup(values);
    action
      .then(() => {
        resetForm();
        setOpenGroupDialog(false);
        setSelectedGroup(undefined);
        setChangeGroups(c => c + 1);
        superAdminService.getAllFriendlyCategoryGroupsWithPagination(0, 100).then(r => setAllGroups(r.rows));
        userNotificationsContext.openSnackbar({ open: true, message: isEdit ? 'Updated successfully' : 'Saved successfully' });
      })
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' }))
      .finally(() => setIsDialogSubmitting(false));
  };

  // Category table
  const categoryHeadCells: IHeadCell[] = [
    { id: 'action', label: 'Action', numeric: false, sortable: false },
    { id: 'name', label: 'Name', numeric: false, sortable: true },
    { id: 'group', label: 'Group', numeric: false, sortable: false },
    { id: 'icon', label: 'Icon', numeric: false, sortable: false },
    { id: 'status', label: 'Visible', numeric: false, sortable: false },
  ];

  const categoryRows = categories.map(c => ({
    id: c.id,
    action: (
      <TableActionButtons
        id={c.id}
        includeEdit
        includeOther
        otherButtonIcon={c.visibleToUser ? IconName.HideSourceOutlined : IconName.CheckCircle}
        otherButtonText={c.visibleToUser ? 'Hide' : 'Show'}
        onEdit={() => { setSelectedCategory(c); setOpenCategoryDialog(true); }}
        onOther={() => toggleCategoryVisibility(c)}
      />
    ),
    name: c.name,
    group: c.friendlyCategoryGroup?.name || '',
    icon: c.icon || '',
    status: c.visibleToUser ? 'Visible' : 'Hidden',
  }));

  const toggleCategoryVisibility = (cat: IFriendlyCategory) => {
    superAdminService.activateFriendlyCategory(cat.id)
      .then(() => {
        setChangeCategories(c => c + 1);
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
      })
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' }));
  };

  const submitCategoryHandler = (values: IPostFriendlyCategory, resetForm: () => void, isEdit: boolean) => {
    if (isDialogSubmitting) return;
    setIsDialogSubmitting(true);
    const action = isEdit && selectedCategory
      ? superAdminService.updateFriendlyCategory(selectedCategory.id, values)
      : superAdminService.createFriendlyCategory(values);
    action
      .then(() => {
        resetForm();
        setOpenCategoryDialog(false);
        setSelectedCategory(undefined);
        setChangeCategories(c => c + 1);
        userNotificationsContext.openSnackbar({ open: true, message: isEdit ? 'Updated successfully' : 'Saved successfully' });
      })
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' }))
      .finally(() => setIsDialogSubmitting(false));
  };

  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc') => {
    if (tab === 0) { setOrderByGroups(orderBy); setOrderGroups(order); }
    else { setOrderByCategories(orderBy); setOrderCategories(order); }
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title='Emission Categories'
        includeBackButton
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
        includeActionButton
        actionButtonText={tab === 0 ? 'Add Group' : 'Add Category'}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={tab === 0 ? () => setOpenGroupDialog(true) : () => setOpenCategoryDialog(true)}
        hideShowActionButton={false}
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Category Groups' />
        <Tab label='Categories' />
      </Tabs>

      <CustomTabPanel value={tab} index={0}>
        <LoadingPage pageReady={!isLoading}>
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileMode}
            headCells={groupHeadCells}
            rows={groupRows}
            selected={[]}
            setSelected={() => {}}
            selectByIcon
            inputPage={pageGroups}
            setInputPage={setPageGroups}
            totalRecords={totalGroups}
            perPage={perPageGroups}
            setPerPage={setPerPageGroups}
          />
        </LoadingPage>
        <AddEditFriendlyCategoryGroupDialog
          open={openGroupDialog}
          closeHandler={() => { setOpenGroupDialog(false); setSelectedGroup(undefined); }}
          isEdit={!!selectedGroup}
          group={selectedGroup}
          isSubmitting={isDialogSubmitting}
          onSubmit={submitGroupHandler}
        />
      </CustomTabPanel>

      <CustomTabPanel value={tab} index={1}>
        <LoadingPage pageReady={!isLoading}>
          <TableDisplay
            onOrderChange={handleOrderChange}
            isMobileMode={isMobileMode}
            headCells={categoryHeadCells}
            rows={categoryRows}
            selected={[]}
            setSelected={() => {}}
            selectByIcon
            inputPage={pageCategories}
            setInputPage={setPageCategories}
            totalRecords={totalCategories}
            perPage={perPageCategories}
            setPerPage={setPerPageCategories}
          />
        </LoadingPage>
        <AddEditFriendlyCategoryDialog
          open={openCategoryDialog}
          closeHandler={() => { setOpenCategoryDialog(false); setSelectedCategory(undefined); }}
          isEdit={!!selectedCategory}
          category={selectedCategory}
          groups={allGroups}
          isSubmitting={isDialogSubmitting}
          onSubmit={submitCategoryHandler}
        />
      </CustomTabPanel>
    </PageOuterContainer>
  );
};
